import { ListPage } from "@services/common/types";
import { Room } from "@services/rooms/types";
import API from "@services/root/rootApi";
import { createSearchParams } from "react-router-dom";
import { Assignee } from "./assignee";
import {
  Meetings,
  MeetingsCount,
  MeetingsFilters,
  MeetingsResponse,
  MeetingInstanceFilters,
  MeetingInstance,
  MeetingElog,
  MeetingRevision,
  ScheduledMeeting,
  MeetingMessage,
  ConferenceParticipant,
  ChannelType,
  ChannelDirection,
  ConsultedRooms,
  CustomRoom,
  Infrastructure,
  ElogBillingCode,
  MeetingCountersResponse,
} from "./types";
import { Location } from "../locations/types";
import { User } from "@services/user/types";

export const getMeetingCounters = async (
  startDate: Date,
  endDate: Date,
  accountIds?: MeetingsFilters["accountId"]
): Promise<MeetingCountersResponse> => {
  const response = await API.post<MeetingCountersResponse>(
    `/symphony-api/meeting/instance/counter`,
    accountIds,
    {
      params: {
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
      },
    }
  );

  return response.data;
};
export const getMeetingSummaries = async (
  startDate: Date,
  endDate: Date,
  maxResults?: number,
  orderBy?: string,
  startIndex?: number,
  totalLimit?: number,
  filters?: MeetingsFilters
): Promise<Meetings> => {
  if (filters?.status?.includes("InProgress")) {
    filters.includeAllInProgress = true;
  }
  const response = await API.post<MeetingsResponse>(
    `/symphony-api/meeting/instance/summary`,
    filters,
    {
      params: {
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
        maxResults,
        orderBy,
        startIndex,
        totalLimit,
      },
    }
  );

  return response.data.meetings;
};

export const getMeetingsCount = async (
  startDate: Date,
  endDate: Date,
  filters?: MeetingInstanceFilters
): Promise<MeetingsCount> => {
  if (filters?.status?.includes("InProgress")) {
    filters.includeAllInProgress = true;
  }
  delete filters?.["start.after"];
  delete filters?.["end.before"];
  const response = await API.post<MeetingsCount>(
    `/symphony-api/meeting/instance/summary/count`,
    filters,
    {
      params: {
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
      },
    }
  );

  return response.data;
};

export const getMeetings = async (
  startDate: Date,
  endDate: Date,
  maxResults?: number,
  startIndex?: number,
  orderBy?: string,
  totalLimit?: number,
  filters?: MeetingInstanceFilters
): Promise<ListPage<MeetingInstance>> => {
  if (filters?.status?.includes("InProgress")) {
    filters.includeAllInProgress = true;
  }

  const response = await API.post<ListPage<MeetingInstance>>(
    `/symphony-api/meeting/instance`,
    filters,
    {
      params: {
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
        maxResults,
        startIndex,
        orderBy,
        totalLimit,
      },
    }
  );

  return response.data;
};

export const getMeetingById = async (meetingId: string) => {
  const response = await API.get<MeetingInstance>(
    `/symphony-api/meeting/instance/${meetingId}`
  );

  return response.data;
};

export const updateMeeting = async (id: number, meeting: MeetingInstance) =>
  await API.post<MeetingInstance>(
    `/symphony-api/meeting/instance/${id}`,
    meeting
  );

export const getAssignees = async (accountId: string[] = []) =>
  (
    await API.get<Assignee[]>(`/symphony-api/meeting/instance/producer`, {
      params: createSearchParams({ accountId }),
    })
  ).data;

export const getParticipantsByAccount = async (accountId: string) =>
  (
    await API.post<ListPage<User>>(
      `/symphony-api/meeting/schedule/participant`,
      {
        accountId,
      }
    )
  ).data;

export const getMeetingElog = async (meetingId: MeetingInstance["id"]) =>
  (
    await API.get<MeetingElog>(
      `/symphony-api/meeting/instance/${meetingId}/elog`
    )
  ).data;
export const putMeetingElog = async (
  meetingId: MeetingInstance["id"],
  elog: Partial<MeetingElog>
) => await API.put(`/symphony-api/meeting/instance/${meetingId}/elog`, elog);
export const getMeetingRevisions = async (
  meetingId: MeetingInstance["meetingId"]
) =>
  (
    await API.get<MeetingRevision[]>(
      `/symphony-api/meeting/schedule/${meetingId}/revision`
    )
  ).data;

export const getMeetingDialInfo = async (
  meetingId: MeetingInstance["id"],
  textOnly?: boolean
) =>
  (
    await API.get<{ dialInfo: string }>(
      `/symphony-api/meeting/schedule/instance/${meetingId}/dialinfo${
        textOnly ? "?textOnly=true" : ""
      }`
    )
  ).data;

export const putMeetingSchedule = async (
  meetingId: MeetingInstance["id"],
  meeting: ScheduledMeeting
) =>
  await API.put<MeetingInstance>(
    `/symphony-api/meeting/schedule/${meetingId}`,
    meeting
  );

export const stopMeeting = async (meetingId: MeetingInstance["id"]) => {
  await API.post<MeetingInstance>(
    `/symphony-api/meeting/instance/${meetingId}/stop`
  );
};

export const extendMeeting = async (
  meetingId: MeetingInstance["id"],
  meetingEnd: number
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/extend?to=${meetingEnd}`
  );
};

export const sendMessage = async (
  meetingId: MeetingInstance["id"],
  message: MeetingMessage
) => {
  await API.post<MeetingInstance>(
    `/symphony-api/meeting/instance/${meetingId}/participant/chat`,
    message
  );
};

export const clearMessages = async (meetingId: MeetingInstance["id"]) => {
  await API.post<MeetingInstance>(
    `/symphony-api/meeting/instance/${meetingId}/participant/chat/clear`
  );
};

export const updateRecordingOwner = async (
  meetingId: MeetingInstance["id"],
  ownerId: string
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/recording/changeowner`,
    { "owner.id": ownerId }
  );
};

export const startRecording = async (
  meetingId: MeetingInstance["id"],
  ownerId: string
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/recording/start`,
    { "owner.id": ownerId }
  );
};

export const stopRecording = async (meetingId: MeetingInstance["id"]) => {
  await API.post(`/symphony-api/meeting/instance/${meetingId}/recording/stop`);
};

export const pauseRecording = async (meetingId: MeetingInstance["id"]) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/recording/pause?isPause=true`
  );
};

export const resumeRecording = async (meetingId: MeetingInstance["id"]) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/recording/pause?isPause=false`
  );
};

export const disconnectReleaseParticipant = async (
  meetingId: MeetingInstance["id"],
  roomId: Room["id"]
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/room/release?roomId=${roomId}&disconnectParticipant=true`
  );
};

export const disconnectParticipant = async (
  meetingId: MeetingInstance["id"],
  participantId: ConferenceParticipant["id"]
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/participant/disconnect`,
    [`${participantId}`]
  );
};

export const muteParticipant = async (
  meetingId: MeetingInstance["id"],
  participantId: ConferenceParticipant["id"],
  channelType: ChannelType,
  mediaChannelDirection: ChannelDirection
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/participant/mute?mediaChannelType=${channelType}&mediaChannelDirection=${mediaChannelDirection}`,
    [`${participantId}`]
  );
};

export const unmuteParticipant = async (
  meetingId: MeetingInstance["id"],
  participantId: ConferenceParticipant["id"],
  channelType: ChannelType,
  mediaChannelDirection: ChannelDirection
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/participant/unmute?mediaChannelType=${channelType}&mediaChannelDirection=${mediaChannelDirection}`,
    [`${participantId}`]
  );
};

export const inviteParticipant = async (
  meetingId: MeetingInstance["id"],
  participant: ConferenceParticipant
) => {
  await API.post(
    `/symphony-api/meeting/instance/${meetingId}/participant/invite`,
    [participant]
  );
};

export const addParticipants = async (
  participants: Array<{ room: { id: string } } | CustomRoom>,
  meetingId?: string
) =>
  await API.post(
    `symphony-api/meeting/instance/${meetingId}/participant/invite`,
    [...participants]
  );

export const getUserFavoritesRooms = async (
  userId?: string
): Promise<ConsultedRooms> =>
  (await API.get(`/symphony-api/meeting/schedule/favorite?userId=${userId}`))
    .data;

export const getUserRecentsRooms = async (
  userId?: string
): Promise<ConsultedRooms> =>
  (await API.get(`/symphony-api/meeting/schedule/recent?userId=${userId}`))
    .data;

export const getAccountLocations = async (
  accountId?: string
): Promise<Array<Location>> =>
  (await API.post(`/symphony-api/meeting/schedule/location`, { accountId }))
    .data;

export const getRoomsByLocation = async (
  accountId?: string,
  locationId?: string
): Promise<Array<Room>> =>
  (
    await API.post(`/symphony-api/meeting/schedule/room`, {
      accountId,
      "location.id": locationId,
    })
  ).data;

export const getRoomsByDisplayName = async (
  accountId?: string,
  name?: string
): Promise<Array<Room>> =>
  (
    await API.post(`/symphony-api/meeting/schedule/room`, {
      accountId,
      name: name,
    })
  ).data;

export const addRoomToFavorites = async (userId?: string, roomId?: string) =>
  await API.post(
    `/symphony-api/meeting/schedule/favorite?userId=${userId}&roomId=${roomId}`
  );

export const removeRoomFromFavorites = async (
  userId?: string,
  roomId?: string
) =>
  await API.delete(
    `/symphony-api/meeting/schedule/favorite?userId=${userId}&roomId=${roomId}`
  );

export const addCustomRoomToFavorites = async (customRoom: CustomRoom) =>
  (
    await API.post(`/symphony-api/meeting/schedule/favorite/custom`, {
      ...customRoom,
    })
  ).data;

export const updateCustomFavorite = async (customRoom: CustomRoom) =>
  await API.put(
    `/symphony-api/meeting/schedule/favorite/custom/${customRoom.id}`,
    {
      ...customRoom,
    }
  );

export const removeCustomRoomFromFavorites = async (customRoomId?: number) =>
  await API.delete(
    `/symphony-api/meeting/schedule/favorite/custom/${customRoomId}`
  );

export const getMeetingInfrastructure = async (
  accountId?: string
): Promise<Infrastructure> =>
  (
    await API.post(`/symphony-api/meeting/schedule/infrastructure`, {
      accountId: [accountId],
    })
  ).data;

export const generateBillingReports = async (
  elogMeeting: Partial<MeetingElog>
): Promise<ElogBillingCode[]> =>
  (
    await API.post(
      `/symphony-api/meeting/instance/${elogMeeting.meetingInstanceId}/elog/generate`,
      elogMeeting
    )
  ).data;
